// (c) chaehongee. Confidential and proprietary.

import { useState, useEffect } from 'react'
import type { FormEvent } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { AlertCircle, X, Eye, EyeOff, Check, ChevronLeft } from 'lucide-react'
import { useAuth } from '../../store/hooks/useAuth'
import { useModalDialog } from '../../hooks/useModalDialog'
import styles from './LoginPage.module.css'

interface LoginPageProps {
    onClose?: () => void
    returnUrl?: string
}

type Step = 'init' | 'login-password' | 'signup-password' | 'signup-check-email'

export function LoginPage({ onClose, returnUrl }: LoginPageProps = {}) {
    const { signInWithGoogle, signInWithApple, signInWithEmail, signUpWithEmail, checkUserExists, user } = useAuth()
    const { t } = useTranslation('auth')
    const [step, setStep] = useState<Step>('init')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

    // Password requirements
    const requirements = [
        { label: t('requirementUppercase'), test: (p: string) => /[A-Z]/.test(p) },
        { label: t('requirementLowercase'), test: (p: string) => /[a-z]/.test(p) },
        { label: t('requirementNumber'), test: (p: string) => /[0-9]/.test(p) },
        { label: t('requirementSpecial'), test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
        { label: t('requirementLength'), test: (p: string) => p.length >= 8 },
    ]

    // Get returnUrl from prop, URL query params, or current pathname
    useEffect(() => {
        const finalReturnUrl = returnUrl || (() => {
            const params = new URLSearchParams(window.location.search)
            return params.get('returnUrl') || window.location.pathname
        })()

        if (finalReturnUrl) {
            sessionStorage.setItem('auth_return_url', finalReturnUrl)
        }
    }, [returnUrl])

    // If user is already authenticated, close the overlay
    useEffect(() => {
        if (user) {
            setLoading(false)
            if (onClose) {
                onClose()
            }
        }
    }, [user, onClose])

    const handleClose = () => {
        if (onClose) {
            onClose()
        } else {
            const storedReturnUrl = sessionStorage.getItem('auth_return_url')
            const finalReturnUrl = returnUrl || storedReturnUrl
            if (finalReturnUrl && finalReturnUrl !== '/login') {
                window.location.href = finalReturnUrl
            } else {
                window.location.href = '/'
            }
        }
    }

    // Focus trap + Escape-to-close + focus return. This overlay mounts only
    // when shown, so it is always "open" while rendered.
    const { dialogRef } = useModalDialog<HTMLDivElement>(true, handleClose)

    const handleGoogleSignIn = async () => {
        setError(null)
        setLoading(true)
        try {
            const storedReturnUrl = sessionStorage.getItem('auth_return_url')
            const finalReturnUrl = returnUrl || storedReturnUrl || undefined
            await signInWithGoogle(finalReturnUrl)
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : t('authFailed')
            setError(errorMessage)
            setLoading(false)
        }
    }

    const handleAppleSignIn = async () => {
        setError(null)
        setLoading(true)
        try {
            const storedReturnUrl = sessionStorage.getItem('auth_return_url')
            const finalReturnUrl = returnUrl || storedReturnUrl || undefined
            await signInWithApple(finalReturnUrl)
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : t('authFailed')
            setError(errorMessage)
            setLoading(false)
        }
    }

    const handleEmailContinue = async (e: FormEvent) => {
        e.preventDefault()
        if (!email) return

        // Basic email validation
        if (!isValidEmail) {
            setError(t('invalidEmail'))
            return
        }

        setError(null)
        setLoading(true)

        try {
            const exists = await checkUserExists(email)
            setStep(exists ? 'login-password' : 'signup-password')
        } catch (err) {
            // Fallback to signup if check fails, or show error
            // For now, assume if check fails, we might have network issue
            void err
            setError(t('verifyEmailFailed'))
        } finally {
            setLoading(false)
        }
    }

    const handleLogin = async (e: FormEvent) => {
        e.preventDefault()
        if (!password) return

        setError(null)
        setLoading(true)

        try {
            await signInWithEmail(email, password)
            // AuthContext effect will handle closure/redirect
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : t('authFailed')
            setError(errorMessage)
            setLoading(false)
        }
    }

    const handleSignup = async (e: FormEvent) => {
        e.preventDefault()

        // Validate password requirements
        const unmet = requirements.filter(r => !r.test(password))
        if (unmet.length > 0) {
            return // Button should be disabled ideally, or show specific error
        }

        setError(null)
        setLoading(true)

        try {
            const { sessionCreated } = await signUpWithEmail(email, password)
            if (!sessionCreated) {
                // Email confirmations enabled: user must confirm before a session exists.
                setStep('signup-check-email')
            }
            // If session was created, AuthContext effect will handle closure/redirect
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : t('signupFailed')
            setError(errorMessage)
        } finally {
            setLoading(false)
        }
    }

    const resetFlow = () => {
        setStep('init')
        setError(null)
        setPassword('')
    }

    const ssoProviders = [
        {
            id: 'google',
            name: 'Google',
            handler: handleGoogleSignIn,
            enabled: true,
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
            ),
        },
        {
            id: 'apple',
            name: 'Apple',
            handler: handleAppleSignIn,
            enabled: true,
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
            ),
        },
    ]

    const enabledProviders = ssoProviders.filter((p) => p.enabled)

    return (
        <>
            <div className={styles.backdrop} onClick={handleClose} aria-hidden="true" />

            <div
                ref={dialogRef}
                className={`${styles.modal} ${loading ? styles.loading : ''}`}
                role="dialog"
                aria-labelledby="login-title"
                aria-modal="true"
                tabIndex={-1}
            >
                <button
                    className={styles.closeButton}
                    onClick={handleClose}
                    aria-label={t('closeLoginModal')}
                >
                    <X className={styles.closeIcon} aria-hidden="true" />
                </button>

                <div className={styles.modalContent}>
                    <h1 id="login-title" className={styles.title}>{t('loginOrSignUp')}</h1>

                    {step === 'init' && (
                        <>
                            <p className={styles.description}>
                                {t('description').split('\n').map((line, i) => (
                                    <span key={i}>{line}{i === 0 && <br />}</span>
                                ))}
                            </p>

                            <div className={styles.ssoButtons}>
                                {enabledProviders.map((provider) => (
                                    <button
                                        key={provider.id}
                                        onClick={provider.handler}
                                        disabled={loading}
                                        className={styles.ssoButton}
                                        aria-label={t('continueWith', { provider: provider.name })}
                                    >
                                        <span className={styles.ssoIcon}>{provider.icon}</span>
                                        <span className={styles.ssoText}>{t('continueWith', { provider: provider.name })}</span>
                                    </button>
                                ))}
                            </div>

                            <div className={styles.divider}>{t('common:or')}</div>

                            <form className={styles.form} onSubmit={handleEmailContinue}>
                                <div className={styles.inputGroup}>
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value)
                                            if (error) setError(null)
                                        }}
                                        className={styles.input}
                                        placeholder={t('emailAddress')}
                                        required
                                        disabled={loading}
                                        aria-label={t('emailAddress')}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className={styles.submitButton}
                                    disabled={loading || !isValidEmail}
                                >
                                    {loading ? t('checking') : t('continue')}
                                </button>
                            </form>
                        </>
                    )}

                    {step === 'login-password' && (
                        <>
                            <form className={styles.form} onSubmit={handleLogin}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{t('email')}</label>
                                    <input className={styles.input} value={email} disabled />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label htmlFor="password" className={styles.label}>{t('password')}</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className={`${styles.input} ${styles.inputWithTrailingIcon}`}
                                            required
                                            autoFocus
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            className={styles.passwordToggle}
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </div>
                                <button type="submit" className={styles.submitButton} disabled={loading || !password}>
                                    {loading ? t('loggingIn') : t('logIn')}
                                </button>
                                <button type="button" onClick={resetFlow} className={styles.backButton}>
                                    <ChevronLeft size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> {t('back')}
                                </button>
                            </form>
                        </>
                    )}

                    {step === 'signup-password' && (
                        <>
                             <form className={styles.form} onSubmit={handleSignup}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{t('email')}</label>
                                    <input className={styles.input} value={email} disabled />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label htmlFor="password" className={styles.label}>{t('password')}</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className={`${styles.input} ${styles.inputWithTrailingIcon}`}
                                            required
                                            autoFocus
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            className={styles.passwordToggle}
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.passwordRequirements}>
                                    {requirements.map((req, i) => {
                                        const met = req.test(password)
                                        return (
                                            <div key={i} className={`${styles.requirement} ${met ? styles.met : ''}`}>
                                                <div className={styles.requirementIcon}>
                                                    {met && <Check size={10} />}
                                                </div>
                                                <span>{req.label}</span>
                                            </div>
                                        )
                                    })}
                                </div>

                                <button
                                    type="submit"
                                    className={styles.submitButton}
                                    disabled={loading || requirements.some(r => !r.test(password))}
                                >
                                    {loading ? t('signingUp') : t('signUp')}
                                </button>
                                <button type="button" onClick={resetFlow} className={styles.backButton}>
                                    <ChevronLeft size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> {t('back')}
                                </button>
                            </form>
                        </>
                    )}

                    {step === 'signup-check-email' && (
                        <>
                            <p className={styles.description}>
                                <Trans
                                    i18nKey="auth:confirmationSent"
                                    values={{ email }}
                                    components={{ strong: <strong />, br: <br /> }}
                                />
                            </p>
                            <button type="button" onClick={resetFlow} className={styles.backButton}>
                                <ChevronLeft size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> {t('back')}
                            </button>
                        </>
                    )}

                    {error && (
                        <div className={styles.errorMessage} role="alert">
                            <AlertCircle className={styles.errorIcon} aria-hidden="true" />
                            <span className={styles.errorText}>{error}</span>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
